import {
  type CurrentPreviousAndNextGamesRes,
  type RelatedScheduleResponse,
  type ScoreboardResponseRelatedScheduleResponseRelatedPagedData,
  type SportFilter,
} from '@on3/api';
import { Filter } from '@on3/ui-lib/components/Filter/Filter';
import { adjustColorForReadability } from '@on3/ui-lib/utils/accessibility/color';
import { slugify } from '@on3/ui-lib/utils/slugify';
import { getHighSchoolSportLabel } from '@on3/ui-lib/utils/url';
import { HighSchoolNav } from '@public/components/HighSchool/Nav/HighSchoolNav';
import { ScoreTile } from '@public/components/HighSchool/Scores/ScoreTile/ScoreTile';
import { On3Avatar } from '@public/components/Image/On3Avatar';
import { MasseyRatings } from '@public/components/UI/MasseyRatings/MasseyRatings';
import clsx from 'clsx';
import { useParams } from 'next/navigation';
import { useCallback, useMemo } from 'react';

import styles from './ScoresPageSingle.module.scss';

interface HighSchoolScoresPageProps {
  filters: {
    sports: SportFilter[];
  };
  scores: CurrentPreviousAndNextGamesRes;
  opponentScores: ScoreboardResponseRelatedScheduleResponseRelatedPagedData;
  title: string;
}

export const HighSchoolScoresPage = ({
  filters,
  scores,
  opponentScores,
  title,
}: HighSchoolScoresPageProps) => {
  const { id, sport } = useParams() as {
    id: string;
    sport: string;
  };
  const {
    relatedModel: { organization },
  } = opponentScores as {
    relatedModel: RelatedScheduleResponse;
  };

  const primaryColor = adjustColorForReadability(
    organization?.primaryColor ?? '#fe3b1f',
  );

  // ? This is here to handle any future filters that may be added
  const handleFilter = useCallback(
    (name: string, value: string) => {
      const segments = ['high-school', id, sport, 'scores'];

      if (name === 'sport') {
        segments[2] = value;
      }

      return `/${segments.join('/')}/`;
    },
    [id, sport],
  );

  const previousGame = useMemo(() => scores.previous, [scores]);
  const nextGame = useMemo(() => scores.next, [scores]);
  const currentGame = useMemo(() => scores.current, [scores]);
  const opponentList = useMemo(
    () => opponentScores.list ?? [],
    [opponentScores],
  );

  return (
    <div className={styles.block}>
      <header className={styles.header}>
        <div className={styles.title}>
          <figure>
            <On3Avatar
              altText={title}
              domain={organization?.defaultAsset?.domain}
              source={organization?.defaultAsset?.source}
              teamAvatar
            />
          </figure>
          <h1>{title}</h1>
        </div>
        <MasseyRatings />
        <nav style={{ color: primaryColor }}>
          <Filter
            name="sport"
            navigate={handleFilter}
            options={filters.sports.map((s) => ({
              label: getHighSchoolSportLabel(s),
              value: slugify(s.name),
            }))}
            value={sport}
          />
          <HighSchoolNav />
        </nav>
      </header>
      <section className={styles.prevNextGames}>
        <div>
          <h2>Last Game</h2>
          {previousGame ? (
            <ScoreTile game={previousGame} sport={sport} />
          ) : (
            <div className={styles.noScores}>No game found</div>
          )}
        </div>
        {currentGame ? (
          <div className={styles.current}>
            <h2>Current Game</h2>
            <ScoreTile game={currentGame} sport={sport} />
          </div>
        ) : (
          <div>
            <h2>Next Game</h2>
            {nextGame ? (
              <ScoreTile game={nextGame} sport={sport} />
            ) : (
              <div className={styles.noScores}>No game found</div>
            )}
          </div>
        )}
      </section>
      <section>
        <h2>Opponents</h2>
        <div className={clsx(styles.scores, styles.opponents)}>
          {opponentList.length > 0 ? (
            <>
              {opponentList.map((game) => (
                <ScoreTile game={game} key={game.key} sport={sport} />
              ))}
            </>
          ) : (
            <div className={styles.noScores}>No scores found</div>
          )}
        </div>
      </section>
    </div>
  );
};
